import { useEffect, useId, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, faNum } from "../game/cards";
import { synth } from "../game/sfx";
import {
  CRUISE_AT, MIN_BRAKE, brakeAngle, brakeMs, coinTier, countdownText, freeAngle, jackpotIndex, landingAfter, landingAngle,
  loadWheel, msLeft, newNonce, oddsText, prizeNote, prizeText, sliceLabel, spinWheel, streakDots, wheel,
  type SpinResult, type WheelSlice, type WheelSpinRow, type WheelState,
} from "../net/wheel";
import { authHref } from "./Account";
import { Overlay } from "./Overlay";

/**
 * گردونه‌ی شانس — دکمه‌ی سربرگ و پنجره‌ی چرخ (docs/lucky-wheel.md).
 *
 * روزی یک چرخشِ رایگان برای حسابِ فعال، و برای مشترک یک چرخشِ دیگر. نتیجه را سرور می‌گیرد؛
 * این‌جا فقط چرخ تا همان خانه می‌چرخد. احتمالِ هر خانه همان عددی است که سرور می‌گوید و
 * زیرِ چرخ نوشته می‌شود: «جور است» شکایتِ اولِ این بازار است و عددِ دیده‌شده جوابش.
 *
 * پیش‌تر یک کارتِ پهن وسطِ لابی بود؛ حالا دکمه‌ای در سربرگ (کاربر، ۲۰ اکتبر ۲۰۲۶): از هر صفحه‌ی
 * لابی پیداست و جای «بازیِ انتخاب‌شده» را بالای صفحه نمی‌گیرد. تا چرخشی مانده، دکمه هر چند ثانیه
 * تکان می‌خورد؛ چرخشِ امروز که زده شد، آرام می‌ایستد — تکانِ همیشگی دیگر دعوت نیست، مزاحمت است.
 * با «کاهشِ حرکت» ِ دستگاه تکان نمی‌خورد و فقط نشانِ «رایگان» می‌مانَد (style.css).
 *
 * مهمان هم دکمه را می‌بیند و پنجره به او «ثبت‌نام کن و هر روز بچرخان» می‌گوید — گردونه دلیلِ
 * خوبی برای ساختنِ حساب است، و حساب لازم است چون دستگاهِ تازه نباید چرخشِ تازه باشد.
 */
export function WheelButton() {
  const s = useSignal(wheel);
  const state = useSignal(client.state);
  const username = useSignal(client.username);
  const [open, setOpen] = useState(false);
  const [, tick] = useState(0);

  // با هر وصل‌شدن دوباره می‌پرسیم: نیمه‌شبِ تهران وسطِ نشست هم می‌افتد — همان قاعده‌ی «امروز».
  useEffect(() => {
    if (state === "online") void loadWheel();
  }, [state]);

  // شمارش هر سی ثانیه؛ نیمه‌شب که رسید، وضعیتِ تازه.
  useEffect(() => {
    const t = setInterval(() => {
      tick((x) => x + 1);
      const cur = wheel.value;
      if (cur && !cur.locked && cur.left === 0 && msLeft(cur) <= 0) void loadWheel();
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  if (!s || !s.enabled || s.slices.length < 2) return null;
  const ready = !s.locked && s.left > 0;
  const line = cardLine(s, username !== "");
  return (
    <>
      <button className={`wheelbtn${ready ? " ready" : ""}`} onClick={() => setOpen(true)}
              aria-label={`گردونه‌ی شانس — ${line}`} title={line}>
        <span className="wheelmini" aria-hidden="true">
          <Disc slices={s.slices} size={30} mini />
        </span>
        <span className="wheellabel">گردونه</span>
        {ready && <span className="wheelfree" aria-hidden="true">{s.left > 1 ? `${fa(s.left)} رایگان` : "رایگان"}</span>}
      </button>
      {/* پرده به ریشه‌ی صفحه می‌رود، نه داخلِ سربرگ: سربرگ backdrop-filter دارد و هر فرزندِ
          fixed را در قابِ خودش زندانی می‌کرد. */}
      {open && createPortal(<WheelDialog onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

/** یک خط درباره‌ی چرخشِ امروز — عنوانِ دکمه‌ی سربرگ و جای خالیِ نتیجه در پنجره. */
export function cardLine(s: WheelState, named: boolean): string {
  if (s.locked) return named ? "ایمیلت را تأیید کن و هر روز رایگان بچرخان" : "ثبت‌نام کن و هر روز رایگان بچرخان";
  if (s.left > 0 && s.bonus_next) return `روز ویژه‌ی زنجیره: جایزه‌ی امروز ${fa(s.streak_mult)} برابر است`;
  if (s.left > 1) return `امروز ${fa(s.left)} چرخش رایگان داری`;
  if (s.left === 1) return "امروز یک چرخش رایگان داری";
  return "چرخش بعدی: " + countdownText(msLeft(s));
}

/** متنِ خطای سرور → جمله: چه شد و چه باید کرد. */
export function wheelError(code: string): string {
  switch (code) {
    case "account_required": return "برای چرخاندن، حساب بساز و ایمیلت را تأیید کن.";
    case "no_spins": return "چرخش امروزت را زدی؛ فردا دوباره بیا.";
    case "wheel_off": return "گردونه فعلاً خاموش است.";
    case "too_many": return "زیاد امتحان کردی؛ کمی بعد دوباره بزن.";
    default: return "به سرور نرسید؛ دوباره بزن. چرخشت دو بار حساب نمی‌شود.";
  }
}

const SPIN_MS_CALM = 600;

function calm(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function WheelDialog({ onClose }: { onClose: () => void }) {
  const s = useSignal(wheel);
  const username = useSignal(client.username);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<SpinResult | null>(null);
  const [error, setError] = useState("");
  const [winAt, setWinAt] = useState(-1);
  const [burst, setBurst] = useState(0);
  // یک کلید برای هر تلاش؛ اگر شبکه افتاد، تلاشِ دوباره **همان** کلید را می‌فرستد و
  // سرور همان نتیجه را برمی‌گرداند، نه چرخشِ دوم.
  const nonce = useRef(newNonce());
  const busy = useRef(false);
  const alive = useRef(true);
  const wait = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinner = useRef<Spinner | null>(null);
  if (!spinner.current) spinner.current = new Spinner();
  useEffect(() => () => {
    alive.current = false;
    spinner.current?.stop();
    if (wait.current) clearTimeout(wait.current);
  }, []);

  if (!s) return null;
  const ready = !s.locked && s.left > 0;

  const go = async () => {
    if (busy.current || s.locked || s.left <= 0) return;
    busy.current = true;
    const sp = spinner.current!;
    const still = calm();
    // چرخ همان لحظه‌ی لمس راه می‌افتد (عقب‌کشیدن و شتاب) و تا رسیدنِ پاسخ می‌چرخد؛ پاسخ که
    // رسید، از همان سرعت ترمز می‌گیرد تا خانه‌ی سرور. جایزه تا ایستادن گفته نمی‌شود.
    sp.start(s.slices.length, still);
    setSpinning(true);
    setError(""); setResult(null); setWinAt(-1);
    const { result: r, error: e } = await spinWheel(nonce.current);
    if (!alive.current) return;
    if (!r) {
      sp.halt();
      busy.current = false;
      setSpinning(false);
      setError(wheelError(e ?? "network"));
      if (e === "no_spins" || e === "wheel_off" || e === "account_required") void loadWheel();
      return;
    }
    nonce.current = newNonce();
    const slices = r.wheel.slices;
    let idx = slices.findIndex((x) => x.id === r.spin.slice_id);
    if (idx < 0) idx = r.spin.slice;
    const j = Math.random(), n = slices.length;
    const ms = sp.land(n, still
      ? (a) => landingAngle(idx, n, a, 1, j)
      : (a) => landingAfter(idx, n, a + MIN_BRAKE, j), still ? SPIN_MS_CALM : 0);
    await new Promise<void>((res) => { wait.current = setTimeout(res, ms + 60); });
    if (!alive.current) return;
    sp.finish();
    setSpinning(false);
    busy.current = false;
    setResult(r);
    setWinAt(idx);
    setBurst((b) => b + 1);
    synth(r.spin.kind === "coins" && !r.spin.bonus ? "chime" : "fanfare");
  };

  const note = result ? prizeNote(result) : "";
  const mode = spinning ? "spin" : result ? "win" : ready ? "idle" : "rest";
  return (
    <Overlay label="گردونه‌ی شانس" base="veil" className="wheelwrap" onClose={() => { if (!busy.current) onClose(); }}>
      <div className="box wheelbox">
        <div className="wheelhead">
          <b>گردونه‌ی شانس</b>
          <span>هر روز رایگان</span>
        </div>
        <div className="wheelstage" data-mode={mode}>
          <span className="wheelrays" aria-hidden="true" />
          <Disc
            slices={s.slices}
            win={result ? winAt : -1}
            rot={spinner.current.rot}
            discRef={(el) => { spinner.current!.disc = el; }}
            pinRef={(el) => { spinner.current!.pin = el; }}
            onHub={ready && !spinning ? () => void go() : undefined}
          />
          {burst > 0 && result && !calm() && <Confetti key={burst} />}
        </div>

        <div className="wheelresult" aria-live="polite">
          {result ? (
            <>
              <span className="wheelwon" key={burst}><PrizeIcon spin={result.spin} />بردی: {prizeText(result.spin)}</span>
              {note && <span className="muted">{note}</span>}
            </>
          ) : error ? (
            <span className="wheelerr">{error}</span>
          ) : spinning ? (
            <span className="muted">می‌چرخد…</span>
          ) : ready || s.locked ? (
            // پیش از چرخاندن جای نتیجه خالی نماند — همان جمله‌ی دکمه‌ی سربرگ.
            <span className="muted">{cardLine(s, username !== "")}</span>
          ) : null}
        </div>

        {s.locked ? (
          // مهمان یا ایمیلِ تأییدنشده: چرخ را می‌بیند و راهِ رسیدن به چرخش را — پیش‌تر همین دکمه روی کارتِ لابی بود.
          <a className="btn wheelgo" href={authHref(username ? "login" : "register")}>{username ? "تأیید ایمیل" : "ثبت‌نام"}</a>
        ) : s.left > 0 ? (
          <button className="btn wheelgo" disabled={spinning} onClick={() => void go()}>
            {s.used > 0 ? "یک چرخش دیگر" : "بچرخان"}
            {s.bonus_next ? ` · ${fa(s.streak_mult)} برابر` : ""}
          </button>
        ) : (
          <div className="muted wheelnext">چرخش بعدی: {countdownText(msLeft(s))}</div>
        )}

        <Streak s={s} />
        {!s.subscriber && s.sub_spins > 0 && (
          <div className="muted wheelsub">مشترک‌ها روزی {fa(1 + s.sub_spins)} بار می‌چرخانند.</div>
        )}

        <details className="wheelodds">
          <summary>احتمال هر خانه</summary>
          <ul>
            {s.slices.map((x, i) => (
              <li key={x.id}>
                <span><i className="sw" style={{ background: sliceColor(s.slices, i) }} />{sliceLabel(x)}</span>
                <span>{oddsText(x.odds)}</span>
              </li>
            ))}
          </ul>
          <p className="muted">نتیجه را سرور می‌گیرد و احتمال‌ها همین‌هاست. چرخش پولی نداریم.</p>
        </details>

        <button className="btn ghost" disabled={spinning} onClick={onClose}>بستن</button>
      </div>
    </Overlay>
  );
}

/** جمله‌ی زنجیره. «زنجیره: ۰ روز» برای بازیکنِ تازه چیزی نمی‌گوید؛ به‌جایش قاعده. */
export function streakText(s: Pick<WheelState, "streak" | "spun_today" | "streak_every" | "streak_mult" | "bonus_next">): string {
  if (s.bonus_next) return `امروز روز ${fa(s.streak_every)}م زنجیره است: جایزه ${fa(s.streak_mult)} برابر.`;
  if (s.streak === 0) return `روزی یک بار بچرخان؛ روز ${fa(s.streak_every)}م پشت‌سرِهم جایزه ${fa(s.streak_mult)} برابر است.`;
  return `زنجیره: ${fa(s.streak)} روز — روز ${fa(s.streak_every)}م جایزه ${fa(s.streak_mult)} برابر است.`;
}

/** نوارِ زنجیره: یک مهره برای هر روز تا روزِ ویژه، که «×۲» دارد. */
function Streak({ s }: { s: WheelState }) {
  if (s.streak_every <= 1 || s.streak_mult <= 1) return null;
  const every = s.streak_every;
  const filled = s.spun_today ? streakDots(s.streak, every) : s.streak % every;
  return (
    <div className="wheelstreak">
      {/* بیش از ده روز در یک خط جا نمی‌شود؛ آن‌وقت فقط جمله می‌مانَد. */}
      {every <= 10 && (
        <ol className="chips" aria-hidden="true">
          {Array.from({ length: every }, (_, i) => {
            const last = i === every - 1;
            const cls = i < filled ? "on" : i === filled && !s.spun_today ? "next" : "";
            return <li key={i} className={`${cls}${last ? " last" : ""}`}>{last ? `×${fa(s.streak_mult)}` : fa(i + 1)}</li>;
          })}
        </ol>
      )}
      <span className="muted">{streakText(s)}</span>
    </div>
  );
}

// ─────────────────────────── چرخ ───────────────────────────

/**
 * رنگِ خانه‌ها، به ترتیب: اناری، فیروزه‌ای، زعفرانی، لاجوردی، پسته‌ای، ارغوانی، گلی، آسمانی.
 * همسایه‌ها گرم و سرد یک‌درمیان‌اند تا مرزِ خانه‌ها از دور هم دیده شود. اپ همین را دارد
 * (WheelV3.SliceColors)؛ جایزه‌ی بزرگ طلایی است ([[jackpotIndex]]).
 */
export const SLICE_COLORS = ["#e2384d", "#17b2ac", "#f39a1e", "#3567d6", "#5fb83a", "#9b4fd6", "#e9558f", "#22a3dc"];
export const JACKPOT_COLOR = "#f7c437";

export function sliceColor(slices: Pick<WheelSlice, "kind" | "amount">[], i: number): string {
  return i === jackpotIndex(slices) ? JACKPOT_COLOR : SLICE_COLORS[i % SLICE_COLORS.length];
}

/** متنِ روی خانه در دو خط: عدد درشت، واحد ریز. */
export function sliceFace(x: Pick<WheelSlice, "kind" | "amount" | "label">): [string, string] {
  if (x.label) return [x.label, ""];
  switch (x.kind) {
    case "coins": return [faNum(x.amount), "سکه"];
    case "gems": return [faNum(x.amount), "گوهر"];
    case "back": return ["پشت کارت", ""];
    case "carpet": return ["فرش", "تازه"];
    case "sub_days": return [`${fa(x.amount)} روز`, "اشتراک"];
    default: return [sliceLabel(x), ""];
  }
}

// اندازه‌ها در واحدِ viewBox ‏(‎−170..170): قاب ۱۵۰، لامپ‌ها ۱۴۳، لبِ تیره ۱۳۵، خانه‌ها ۱۳۲.
const R_FRAME = 150, R_BULB = 143, R_LIP = 135, R = 132, R_PEG = 125, BULBS = 16;

/** نقطه روی دایره: زاویه از بالا و ساعت‌گرد. */
function xy(deg: number, rad: number): [number, number] {
  const a = ((deg - 90) * Math.PI) / 180;
  return [rad * Math.cos(a), rad * Math.sin(a)];
}

function wedge(a0: number, seg: number, r: number): string {
  const [x0, y0] = xy(a0, r), [x1, y1] = xy(a0 + seg, r);
  return `M 0 0 L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${r} ${r} 0 ${seg > 180 ? 1 : 0} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

/** رنگِ روشن‌تر (k>0) یا تیره‌تر (k<0) — برای سایه‌روشنِ خانه. */
function shade(hex: string, k: number): string {
  const v = parseInt(hex.slice(1), 16);
  const ch = (x: number) => Math.round(k >= 0 ? x + (255 - x) * k : x * (1 + k));
  const [r, g, b] = [(v >> 16) & 255, (v >> 8) & 255, v & 255].map(ch);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/** اندازه‌ی قلم تا متن در پهنای w جا شود (تخمین؛ رقم و حرفِ فارسیِ پررنگ حدودِ ۰٫۵۸ em). */
function fit(text: string, w: number, max: number): number {
  return Math.max(8, Math.min(max, w / (Math.max(1, text.length) * 0.58)));
}

type DiscProps = {
  slices: WheelSlice[];
  /** px؛ خالی = همه‌ی پهنای ظرف. */
  size?: number;
  /** نسخه‌ی ریزِ کارت: بی متن، بی لامپ و بی نشانگر. */
  mini?: boolean;
  /** خانه‌ی برده که می‌درخشد؛ بقیه کمی تیره می‌شوند. */
  win?: number;
  rot?: number;
  discRef?: (el: HTMLDivElement | null) => void;
  pinRef?: (el: HTMLDivElement | null) => void;
  /** توپیِ وسط هم دکمه‌ی چرخاندن است — «خودِ چیز را بزن». */
  onHub?: () => void;
};

const VB = "-170 -170 340 340";

/**
 * خودِ چرخ. خانه‌ی i از زاویه‌ی i·seg (از بالا، ساعت‌گرد) شروع می‌شود — همان قراردادِ
 * [[landingAngle]].
 *
 * لایه‌لایه است و فقط لایه‌ی خانه‌ها (`wspin`، یک div) می‌چرخد، با transform ِ CSS: مرورگر
 * آن را یک بار می‌کشد و بعد فقط می‌چرخاندش (کارتِ گرافیک)، نه این‌که هر فریم کلِ SVG را با
 * گرادیان و عکس و متن دوباره بکشد — روی گوشیِ ارزان همین فرقِ روان و لرزان است. قاب پشتش،
 * برق و لامپ‌ها رویش، و توپی و نشانگر هر کدام لایه‌ی خودشان را دارند.
 */
function Disc({ slices, size, mini = false, win = -1, rot = 0, discRef, pinRef, onHub }: DiscProps) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const n = slices.length, seg = 360 / n;
  const jp = jackpotIndex(slices);
  const fill = (i: number) => (i === jp ? `url(#${id}-jp)` : `url(#${id}-g${i % SLICE_COLORS.length})`);
  const box = size ? ({ inlineSize: size, blockSize: size } as CSSProperties) : undefined;
  const rim = (
    <linearGradient id={`${id}-rim`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#fff0b8" />
      <stop offset="0.42" stopColor="#ebb54a" />
      <stop offset="1" stopColor="#9a5b15" />
    </linearGradient>
  );
  return (
    <div className={`wheeldisc${mini ? " mini" : ""}`} style={box} role="img" aria-label="گردونه">
      {/* قاب: لبه‌ی تیره، حلقه‌ی طلاییِ فلزی، و لبِ تیره‌ی زیرِ خانه‌ها */}
      <svg className="wl wback" viewBox={VB} aria-hidden="true">
        <defs>{rim}</defs>
        <circle r={R_FRAME + 2} fill="#4a2a0c" />
        <circle r={R_FRAME - 1} fill={`url(#${id}-rim)`} />
        <circle r={R_LIP} fill="#3b1f08" />
      </svg>

      <div className="wl wspin" ref={discRef} style={{ transform: `rotate(${rot.toFixed(2)}deg)` }}>
        <svg viewBox={VB} aria-hidden="true">
          <defs>
            {SLICE_COLORS.map((c, i) => (
              <radialGradient key={i} id={`${id}-g${i}`} gradientUnits="userSpaceOnUse" cx="0" cy="0" r={R}>
                <stop offset="0.22" stopColor={shade(c, -0.42)} />
                <stop offset="0.78" stopColor={c} />
                <stop offset="1" stopColor={shade(c, 0.16)} />
              </radialGradient>
            ))}
            <radialGradient id={`${id}-jp`} gradientUnits="userSpaceOnUse" cx="0" cy="0" r={R}>
              <stop offset="0.2" stopColor="#a4520a" />
              <stop offset="0.7" stopColor={JACKPOT_COLOR} />
              <stop offset="1" stopColor="#fff0b0" />
            </radialGradient>
            {/* سایه‌ی زیرِ نقش‌ها: سکه‌ی طلایی روی خانه‌ی طلایی هم جدا دیده شود. */}
            <filter id={`${id}-sh`} x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="1.6" stdDeviation="1.3" floodColor="#1a0800" floodOpacity="0.55" />
            </filter>
          </defs>
          {slices.map((x, i) => <path key={x.id} d={wedge(i * seg, seg, R)} fill={fill(i)} />)}
          {slices.map((x, i) => {
            const [x0, y0] = xy(i * seg, mini ? 0 : 30), [x1, y1] = xy(i * seg, R);
            return <line key={"l" + x.id} x1={x0} y1={y0} x2={x1} y2={y1} className="wsep" strokeWidth={mini ? 7 : 2.6} />;
          })}
          {!mini && slices.map((x, i) => <Face key={"f" + x.id} slice={x} mid={(i + 0.5) * seg} seg={seg} jackpot={i === jp} shadow={`url(#${id}-sh)`} />)}
          {!mini && slices.map((x, i) => {
            const [px, py] = xy(i * seg, R_PEG);
            return <circle key={"p" + x.id} cx={px} cy={py} r="3.6" className="wpeg" />;
          })}
          {win >= 0 && win < n && (
            <>
              {slices.map((x, i) => i !== win && <path key={"d" + x.id} d={wedge(i * seg, seg, R)} className="wdim" />)}
              <path d={wedge(win * seg, seg, R - 1.5)} className="wwin" />
            </>
          )}
        </svg>
      </div>

      {/* سایه‌ی لبه، برقِ بالا و لامپ‌ها ثابت‌اند: نور از بالا می‌آید، نه با چرخ. */}
      <svg className="wl wfront" viewBox={VB} aria-hidden="true">
        <defs>
          <radialGradient id={`${id}-edge`} gradientUnits="userSpaceOnUse" cx="0" cy="0" r={R}>
            <stop offset="0.82" stopColor="#000" stopOpacity="0" />
            <stop offset="1" stopColor="#000" stopOpacity="0.42" />
          </radialGradient>
          <linearGradient id={`${id}-gloss`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fff" stopOpacity="0.34" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle r={R} fill={`url(#${id}-edge)`} />
        <ellipse cx="0" cy={-R * 0.42} rx={R * 0.82} ry={R * 0.5} fill={`url(#${id}-gloss)`} />
        {!mini && Array.from({ length: BULBS }, (_, i) => {
          const [bx, by] = xy(i * (360 / BULBS), R_BULB);
          return (
            <g key={i} className={`wb ${i % 2 ? "b" : "a"}`} transform={`translate(${bx.toFixed(2)} ${by.toFixed(2)})`}>
              <circle className="wbhalo" r="7.4" />
              <circle className="wbcore" r="4.2" />
            </g>
          );
        })}
      </svg>

      <div className={`wl whub${onHub ? " live" : ""}`} onClick={onHub}>
        <svg viewBox={`${-HUB_BOX} ${-HUB_BOX} ${2 * HUB_BOX} ${2 * HUB_BOX}`} aria-hidden="true">
          <defs>
            {rim}
            <radialGradient id={`${id}-gem`} cx="0.38" cy="0.32" r="0.78">
              <stop offset="0" stopColor="#ff9a8f" />
              <stop offset="0.5" stopColor="#e2384d" />
              <stop offset="1" stopColor="#7d1022" />
            </radialGradient>
          </defs>
          <circle r={mini ? 40 : 32} fill="#4a2a0c" />
          <circle r={mini ? 37 : 29.5} fill={`url(#${id}-rim)`} />
          <circle r={mini ? 28 : 22.5} fill={`url(#${id}-gem)`} />
          {!mini && <image href={ART.star} x="-13" y="-14" width="26" height="26" />}
          <ellipse cx={mini ? -8 : -6} cy={mini ? -12 : -10} rx={mini ? 11 : 8} ry={mini ? 6 : 4.5} fill="#fff" opacity="0.35" />
        </svg>
      </div>

      {!mini && <Pointer pinRef={pinRef} />}
    </div>
  );
}

/** نیم‌پهنای لایه‌ی توپی (واحدِ viewBox)؛ CSS ِ .whub با همین درصد جایش را می‌گیرد. */
const HUB_BOX = 42;

/** متن و نقشِ یک خانه، چرخیده تا «بالا» ی آن رو به لبه باشد. */
function Face({ slice, mid, seg, jackpot, shadow }: { slice: WheelSlice; mid: number; seg: number; jackpot: boolean; shadow: string }) {
  const [big, small] = sliceFace(slice);
  const chord = (r: number) => 2 * r * Math.sin(((seg / 2) * Math.PI) / 180);
  const iconR = 99, bigR = 67, smallR = 49;
  const icon = Math.min(40, chord(iconR) * 0.56);
  const jp = jackpot ? " jp" : "";
  // نامِ دوکلمه‌ایِ بی واحد («پشتِ کارت») در یک خط ریز می‌شد؛ دو خطِ درشت خواناتر است.
  const lines: [string, string, boolean] = small ? [big, small, false] : big.includes(" ") && big.length > 6
    ? [big.slice(0, big.indexOf(" ")), big.slice(big.indexOf(" ") + 1), true] : [big, "", false];
  const [l1, l2, two] = lines;
  return (
    <g transform={`rotate(${mid.toFixed(2)})`}>
      <g filter={shadow}><SliceIcon slice={slice} y={-iconR} s={icon} /></g>
      <text y={-bigR} className={`wbig${jp}`} fontSize={fit(l1, chord(bigR) * 0.84, two ? 17 : 21).toFixed(1)} textAnchor="middle" dominantBaseline="central">{l1}</text>
      {l2 && (
        <text y={-smallR} className={`${two ? "wbig" : "wsmall"}${jp}`} fontSize={fit(l2, chord(smallR) * 0.9, two ? 15 : 12).toFixed(1)} textAnchor="middle" dominantBaseline="central">{l2}</text>
      )}
    </g>
  );
}

const ART = {
  coin: "/art/ui/wheel-coin.webp",
  gem: "/art/ui/wheel-gem.webp",
  star: "/art/ui/wheel-star.webp",
  back: "/art/table/back.webp",
  carpet: "/art/table/carpet-thumb.webp",
};

/** نقشِ خانه: سکه‌ها به اندازه‌ی جایزه روی هم، پشتِ کارتِ واقعی، فرش، ستاره برای اشتراک. */
function SliceIcon({ slice, y, s }: { slice: WheelSlice; y: number; s: number }) {
  if (slice.kind === "coins" || slice.kind === "gems") {
    const k = slice.kind === "coins" ? coinTier(slice.amount) : slice.amount >= 50 ? 3 : slice.amount >= 10 ? 2 : 1;
    const href = slice.kind === "coins" ? ART.coin : ART.gem;
    // از عقب به جلو: سکه‌ی وسطِ ردیفِ سه‌تایی بالاتر و جلوتر است.
    const at: [number, number, number][] = k === 1 ? [[0, 0, 1]] : k === 2 ? [[-0.24, 0.06, 0.86], [0.24, -0.04, 0.86]] : [[-0.3, 0.1, 0.78], [0.3, 0.1, 0.78], [0, -0.1, 0.86]];
    return (
      <g transform={`translate(0 ${y.toFixed(2)})`}>
        {at.map(([dx, dy, k2], i) => {
          const w = s * k2;
          return <image key={i} href={href} x={(dx * s - w / 2).toFixed(2)} y={(dy * s - w / 2).toFixed(2)} width={w.toFixed(2)} height={w.toFixed(2)} />;
        })}
      </g>
    );
  }
  if (slice.kind === "back") {
    const h = s * 1.12, w = h * 0.69;
    return (
      <g transform={`translate(0 ${y.toFixed(2)}) rotate(-12)`}>
        <rect x={-w / 2 - 2} y={-h / 2 - 2} width={w + 4} height={h + 4} rx="4" fill="#fff8e8" />
        <image href={ART.back} x={-w / 2} y={-h / 2} width={w} height={h} preserveAspectRatio="xMidYMid slice" />
      </g>
    );
  }
  if (slice.kind === "carpet") {
    const w = s * 1.1;
    return (
      <g transform={`translate(0 ${y.toFixed(2)})`}>
        <rect x={-w / 2 - 2} y={-w * 0.36 - 2} width={w + 4} height={w * 0.72 + 4} rx="4" fill="#fff8e8" />
        <image href={ART.carpet} x={-w / 2} y={-w * 0.36} width={w} height={w * 0.72} preserveAspectRatio="xMidYMid slice" />
      </g>
    );
  }
  return <image href={ART.star} x={-s / 2} y={y - s / 2} width={s} height={s} />;
}

/**
 * نشانگر: قطره‌ی طلایی با نگینِ اناری؛ سرش روی قاب، نوکش در خانه. لایه‌ی خودش را دارد تا
 * تکانِ سرِ هر میخ هم فقط چرخاندنِ همان لایه باشد. محورِ تکان مرکزِ سرِ قطره است (CSS ِ .wpin).
 */
function Pointer({ pinRef }: { pinRef?: (el: HTMLDivElement | null) => void }) {
  // دایره‌ی ۱۴ به مرکزِ (۰،۰) و نوک در (۰،۳۰): نقطه‌های مماس در ±۶۲° ِ زیرِ مرکز.
  const d = "M 0 30 L -12.38 6.53 A 14 14 0 1 1 12.38 6.53 Z";
  return (
    <div className="wl wpin" ref={pinRef}>
      <svg viewBox="-17 -17 34 52" aria-hidden="true">
        <path d={d} transform="translate(0 3)" fill="#000" opacity="0.35" />
        <path d={d} fill="#ebb54a" stroke="#4a2a0c" strokeWidth="3" strokeLinejoin="round" />
        <path d="M -8 -6 A 10 10 0 0 1 8 -6" fill="none" stroke="#fff3c4" strokeWidth="2.4" strokeLinecap="round" opacity="0.8" />
        <circle r="6.6" fill="#c8283c" stroke="#7d1022" strokeWidth="1.6" />
        <circle cx="-2" cy="-2.2" r="2" fill="#fff" opacity="0.85" />
      </svg>
    </div>
  );
}

/** نقشِ کوچکِ کنارِ «بردی: …». */
function PrizeIcon({ spin }: { spin: Pick<WheelSpinRow, "kind" | "fallback"> }) {
  const kind = spin.fallback ? "coins" : spin.kind;
  const src = kind === "coins" ? ART.coin : kind === "gems" ? ART.gem : kind === "back" ? ART.back : kind === "carpet" ? ART.carpet : ART.star;
  return <img className={`wprize ${kind}`} src={src} alt="" aria-hidden="true" />;
}

/** کاغذرنگی از نوکِ نشانگر — فقط با حرکتِ روشن. */
function Confetti() {
  const bits = useMemo(() => Array.from({ length: 36 }, (_, i) => {
    const a = (-170 + Math.random() * 160) * (Math.PI / 180);
    const d = 80 + Math.random() * 110;
    return {
      "--x": `${(Math.cos(a) * d).toFixed(0)}px`,
      "--y": `${(Math.sin(a) * d + 70 + Math.random() * 90).toFixed(0)}px`,
      "--r": `${(Math.random() * 900 - 450).toFixed(0)}deg`,
      "--d": `${(Math.random() * 0.12).toFixed(2)}s`,
      background: i % 5 === 0 ? JACKPOT_COLOR : SLICE_COLORS[i % SLICE_COLORS.length],
    } as CSSProperties;
  }), []);
  return <span className="wconfetti" aria-hidden="true">{bits.map((st, i) => <i key={i} style={st} />)}</span>;
}

// ─────────────────────────── چرخاننده ───────────────────────────

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
// یا فریم یا زمان‌سنج، نه هر دو: شماره‌ی فریم و زمان‌سنج دو فضای جدا دارند و clearTimeout با
// شماره‌ی فریم ممکن بود زمان‌سنجِ دیگری (مثلاً پایانِ چرخش) را ببُرد.
const RAF = typeof requestAnimationFrame === "function" && typeof cancelAnimationFrame === "function";
const frame = (f: () => void): number => (RAF ? requestAnimationFrame(f) : (setTimeout(f, 16) as unknown as number));
const unframe = (h: number): void => {
  if (RAF) cancelAnimationFrame(h);
  else clearTimeout(h as unknown as ReturnType<typeof setTimeout>);
};

/**
 * چرخاننده‌ی چرخ با requestAnimationFrame. زاویه را مستقیم روی transform ِ لایه‌ی `wspin`
 * می‌نویسد (نه با state، که هر فریم React را دوباره بکشد)، سرِ هر میخ تیک می‌زند و نشانگر را
 * تکان می‌دهد.
 * پایانِ چرخش را زمان‌سنجِ خودِ پنجره اعلام می‌کند (finish)؛ فریمِ جامانده نتیجه را دیر نمی‌کند.
 */
class Spinner {
  rot = 0;
  disc: HTMLDivElement | null = null;
  pin: HTMLDivElement | null = null;
  private t0 = 0;
  private from = 0;
  private brake: { at: number; a: number; b: number; ms: number } | null = null;
  private handle = 0;
  private seg = 45;
  private lastSeg = 0;
  private lastTick = -1e9;
  private kick = -1e9;
  private still = false;

  /** لمس: چرخِ آزاد راه می‌افتد (با حرکتِ آرام، تا پاسخ نمی‌جنبد). */
  start(count: number, still: boolean): void {
    this.stop();
    this.seg = 360 / Math.max(1, count);
    this.still = still;
    this.brake = null;
    this.t0 = now();
    this.from = this.rot;
    this.lastSeg = Math.floor(this.rot / this.seg);
    if (!still) this.run();
  }

  /**
   * پاسخ رسید: ترمز تا target(زاویه‌ی آغازِ ترمز). برمی‌گرداند چند میلی‌ثانیه‌ی دیگر می‌ایستد.
   * calmMs > 0 = حرکتِ آرام: بی چرخِ آزاد، فقط سُرخوردنی کوتاه تا خانه.
   */
  land(count: number, target: (a: number) => number, calmMs = 0): number {
    this.seg = 360 / Math.max(1, count);
    if (calmMs > 0 || this.still) {
      const a = this.rot, ms = calmMs || SPIN_MS_CALM;
      this.t0 = now(); this.from = a;
      this.brake = { at: 0, a, b: target(a), ms };
      this.run();
      return ms;
    }
    const t = now() - this.t0;
    const at = Math.max(t, CRUISE_AT);
    const a = freeAngle(this.from, at), b = target(a);
    this.brake = { at, a, b, ms: brakeMs(a, b) };
    if (!this.handle) this.run();
    return at - t + this.brake.ms;
  }

  /** پاسخی نیامد: روی نزدیک‌ترین میخِ جلو بایست — نشانگر روی مرزِ دو خانه یعنی «هیچ». */
  halt(): void {
    if (this.still || !this.handle) { this.stop(); return; }
    const seg = this.seg;
    this.land(360 / seg, (a) => Math.ceil((a + 150) / seg) * seg);
  }

  /** همین حالا روی مقصد بایست. */
  finish(): void {
    const b = this.brake;
    this.stop();
    if (b) this.set(b.b);
    this.brake = null;
    if (this.pin) this.pin.style.transform = "";
  }

  stop(): void {
    if (this.handle) unframe(this.handle);
    this.handle = 0;
  }

  private run(): void {
    this.stop();
    const step = () => {
      const t = now() - this.t0, b = this.brake;
      if (b && t >= b.at + b.ms) { this.handle = 0; this.set(b.b); return; }
      this.set(b && t >= b.at ? brakeAngle(b.a, b.b, t - b.at, b.ms) : freeAngle(this.from, t));
      this.handle = frame(step);
    };
    this.handle = frame(step);
  }

  private set(a: number): void {
    this.rot = a;
    if (this.disc) this.disc.style.transform = `rotate(${a.toFixed(2)}deg)`;
    const k = Math.floor(a / this.seg), t = now();
    if (k !== this.lastSeg) {
      this.lastSeg = k;
      this.kick = t;
      // سقفِ تیک: اولِ چرخش خانه‌ها پشتِ هم رد می‌شوند و صدا باید آرام بماند.
      if (t - this.lastTick > 55) { this.lastTick = t; synth("tick", { volume: 0.45 }); }
    }
    const u = Math.max(0, 1 - (t - this.kick) / 150);
    if (this.pin) this.pin.style.transform = u > 0 ? `rotate(${(-24 * u * u).toFixed(2)}deg)` : "";
  }
}
